<template>
  <div id="mapContainer" ref="mapContainer" class="map-canvas" data-testId="mapContainer" />
</template>
<script setup lang="ts">
/* eslint-disable max-lines */
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import uniqby from 'lodash.uniqby';
import {
  type ExpressionSpecification,
  type FeatureSelector,
  Map,
  type MapMouseEvent,
  NavigationControl,
  Popup,
} from 'mapbox-gl';
import { storeToRefs } from 'pinia';
import { computed, inject, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { getPriceDistribution } from '@/api/fetchData';
import useAfterRouterLocationChange from '@/composables/useAfterRouterLocationChange';
import useRouterLocationPush from '@/composables/useRouterLocationPush';
import { CustomAction } from '@/helpers/datadogRUMService';
import { mapLayerToGeoLevel } from '@/helpers/geo/geoMapping';
import { getCulture } from '@/helpers/localizeLanguage';
import { isPlatformIs24 } from '@/helpers/platform';
import { propertyTypeToValue } from '@/helpers/propertyTypeMapping';
import { type Culture, LevelValue } from '@/models/iazi/common/enums';
import {
  type PriceDistributionData,
  type PriceDistributionDataItem,
  type PriceDistributionResponse,
} from '@/models/iazi/queries/getPriceDistributionResponse';
import useMapStore from '@/stores/useMapStore';
import {
  EventSource,
  MapGeoLevel,
  MapLayerHighlightName,
  MapLayerName,
  MapLayerSource,
  ZOOM_CONFIG,
} from '@/types';
import {
  CANTON_MAX_ZOOM_DESKTOP,
  CANTON_MAX_ZOOM_MOBILE,
  CANTONTILESETID,
  DEFAULT_MIN_ZOOM,
  FILL_OPACITY,
  LINECOLOR_HOVER,
  MAP_LARGE_SCREEN_ZOOM,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  MUNICIPALITY_MAX_ZOOM_DESKTOP,
  MUNICIPALITY_MAX_ZOOM_MOBILE,
  MUNICIPALITYTILESETID,
  OPACITY_MAX,
  SWITZERLAND_COORDINATE,
  TOWNTILESETID,
} from '@/utils/constants';
import {
  addHighlightLayer,
  addLayer,
  clearHighlightLayer,
  HoverHighlightLayer,
  layerHighliter,
  setInitialPaintProperties,
  setLayerVisibility,
  sortLegendColors,
} from '@/utils/functions';
import { type MediaContext } from '@/views/composables/useMedia';
import { captureAction } from '@smg-real-estate/browser-rum';

const emit = defineEmits(['mapInteraction']);

const { isLargeScreen, isSmallScreen, isTabletScreen } = inject<MediaContext>('mediaScreen')!;
const mapStore = useMapStore();
const {
  data,
  viewMode,
  polygon,
  priceDistributions,
  eventSource,
  currentLevel,
  propertyType,
  selectedPriceDistributions,
} = storeToRefs(mapStore);
const { t, locale } = useI18n();
const route = useRoute();

const { pushLocation } = useRouterLocationPush();
const { updateMapAndData } = useAfterRouterLocationChange();

let map: Map | null = null;
const popup = new Popup({ offset: 5, closeButton: false });
const zoom = computed<number>(() => (isLargeScreen.value ? MAP_LARGE_SCREEN_ZOOM : MAP_MIN_ZOOM));
const culture = computed<Culture>(() => getCulture(locale.value as HgSupportedLanguage));
const levelMinZoom = computed<number>(() => {
  const screenType = isLargeScreen.value ? 'largeScreen' : 'noneLargeScreen';
  const levelZoom = ZOOM_CONFIG[screenType][currentLevel.value];
  return levelZoom || DEFAULT_MIN_ZOOM;
});

const currentZoomLevel = computed<MapGeoLevel>(() => {
  const zoomLevel = map?.getZoom();
  if (
    zoomLevel &&
    (isLargeScreen.value ? MUNICIPALITY_MAX_ZOOM_DESKTOP : MUNICIPALITY_MAX_ZOOM_MOBILE) < zoomLevel
  ) {
    return MapGeoLevel.Municipality;
  } else if (
    zoomLevel &&
    (isLargeScreen.value ? CANTON_MAX_ZOOM_DESKTOP : CANTON_MAX_ZOOM_MOBILE) < zoomLevel
  ) {
    return MapGeoLevel.Canton;
  } else {
    return MapGeoLevel.Country;
  }
});

watch(viewMode, () => {
  map?.resize();
});
watch(isLargeScreen, () => {
  map?.resize();
});
watch(isSmallScreen, () => {
  map?.resize();
});
watch(isTabletScreen, () => {
  map?.resize();
});
watch(locale, () => {
  setTimeout(() => {
    // waiting for internal changes are done
    const refreshColor = false;
    const routeLocale = route.meta.locale;
    const needsRoutePush = routeLocale !== locale.value;
    setInitialProperties(needsRoutePush, refreshColor);
  });
});
watch(zoom, () => {
  if (currentLevel.value === MapGeoLevel.Country && !polygon.value.current) {
    map?.setZoom(zoom.value);
  }
});
watch(polygon, () => {
  if (eventSource.value === EventSource.Reset) {
    clearHighlightLayer(map as Map);
    map?.zoomTo(zoom.value);
  } else {
    updateLayer();
  }
});
watch(priceDistributions, () => {
  const refreshColor = false;
  loadCantonLayer(true);
  loadMunicipalityLayer(refreshColor);
  loadTownLayer(refreshColor);
  updatePolygonHiding(refreshColor);
});

onMounted(() => {
  initializeMap();
});
const updatePolygonHiding = async (refreshColor: boolean) => {
  const currentProperties = polygon.value.current?.properties;
  const previousProperties = polygon.value.previous?.properties;
  let fetchMunicipalityLayers: boolean =
    (refreshColor && currentProperties?.cantonCode !== previousProperties?.cantonCode) ||
    (currentProperties?.cantonCode && !refreshColor);
  let fetchTownLayers: boolean =
    (refreshColor && currentProperties?.municipalityId !== previousProperties?.municipalityId) ||
    (currentProperties?.municipalityId && !refreshColor);

  const promises: Promise<void>[] = [];
  if (fetchMunicipalityLayers) {
    promises.push(
      fetchAndSetPolygonData(
        LevelValue.Municipality,
        MapGeoLevel.Canton,
        polygon.value.current?.properties?.cantonId,
      ),
    );
  }
  if (fetchTownLayers) {
    promises.push(
      fetchAndSetPolygonData(
        LevelValue.Town,
        MapGeoLevel.Municipality,
        polygon.value.current?.properties?.municipalityId,
      ),
    );
  }
  await Promise.all(promises);
};
const loadCantonLayer = (refreshColor: boolean) => {
  loadLayer({
    layerSource: MapLayerSource.Canton,
    layerUrlId: CANTONTILESETID,
    promoteId: 'cantonCode',
    layerName: MapLayerName.CantonTileSet,
    layerHighlightName: MapLayerHighlightName.CantonHighlight,
    geoData: priceDistributions.value[MapGeoLevel.Canton],
    refreshColors: refreshColor,
  });
};
const loadMunicipalityLayer = (refreshColor: boolean) => {
  loadLayer({
    layerSource: MapLayerSource.Municipality,
    layerUrlId: MUNICIPALITYTILESETID,
    promoteId: 'municipalityId',
    layerName: MapLayerName.MunicipalityTileSet,
    layerHighlightName: MapLayerHighlightName.MunicipalityHighlight,
    geoData: priceDistributions.value[MapGeoLevel.Municipality],
    refreshColors: refreshColor,
  });
};
const loadTownLayer = (refreshColor: boolean) => {
  loadLayer({
    layerSource: MapLayerSource.Town,
    layerUrlId: TOWNTILESETID,
    promoteId: 'onrp',
    layerName: MapLayerName.TownTileSet,
    layerHighlightName: MapLayerHighlightName.TownHighlight,
    geoData: priceDistributions.value[MapGeoLevel.Town],
    refreshColors: refreshColor,
  });
};
const loadLayer = (params: {
  layerSource: MapLayerSource;
  layerUrlId: string;
  promoteId: string;
  layerName: MapLayerName;
  layerHighlightName: MapLayerHighlightName;
  geoData: PriceDistributionData | null;
  refreshColors: boolean;
}) => {
  if (!map) {
    return;
  }
  if (!map.getSource(params.layerSource)) {
    map.addSource(params.layerSource, {
      type: 'vector',
      url: params.layerUrlId,
      promoteId: params.promoteId,
    });
  }
  if (!map.getLayer(params.layerName)) {
    handleAddLayer(params.layerName, params.layerSource);
  }
  if (!map.getLayer(params.layerHighlightName)) {
    HoverHighlightLayer(map as Map, params.layerHighlightName, params.layerSource);
  }
  if (params.refreshColors) {
    addLayerColorDistribution(
      params.layerName,
      params.layerSource,
      params.layerHighlightName,
      params.geoData,
    );
  } else {
    setPopupData(params.layerName, params.layerSource, params.layerHighlightName, params.geoData);
  }
};
const handleAddLayer = (tileSetName: MapLayerName, layerSource: MapLayerSource): void => {
  addLayer({
    map: map as Map,
    tileSetName,
    layerSource,
    municipalityTileSetName: MapLayerName.MunicipalityTileSet,
    townTileSetName: MapLayerName.TownTileSet,
    cantonTileSetName: MapLayerName.CantonTileSet,
  });
};
const setPopupData = (
  tileSetName: MapLayerName,
  layerSource: MapLayerSource,
  tileSetHighlightName: MapLayerHighlightName,
  priceDistributionData: PriceDistributionData | null,
) => {
  if (!priceDistributionData) return;

  if (isLargeScreen.value) {
    layerHighliter({
      map: map as Map,
      source: layerSource,
      layer: tileSetName,
      layerHighlightName: tileSetHighlightName,
      priceData: priceDistributionData,
      popup,
      culture: culture.value,
      insufficientData: t('map.insufficientData'),
    });
  }

  const deduplicatedData = uniqby(priceDistributionData.data, 'geoId');
  deduplicatedData.forEach((row: PriceDistributionDataItem) => {
    const priceValue = isPlatformIs24() ? row.fValue : Math.round(row.fValue / 100) * 100;
    map?.setFeatureState(
      {
        source: layerSource,
        sourceLayer: tileSetName,
        id: row.geoId || undefined,
      } as FeatureSelector,
      {
        geoName: row.geoName,
        colorValue: row.colorValue, //TODO bring color logic to UI to match with Scale
        price: priceValue,
      },
    );
  });
};
const addLayerColorDistribution = (
  tileSetName: MapLayerName,
  layerSource: MapLayerSource,
  tileSetHighlightName: MapLayerHighlightName,
  priceDistributionData: PriceDistributionData | null,
) => {
  if (!data || !priceDistributionData) {
    return;
  }
  setPopupData(tileSetName, layerSource, tileSetHighlightName, priceDistributionData);
  const deduplicatedData = uniqby(priceDistributionData.data, 'geoId');
  let matchExpression: ExpressionSpecification = ['match', ['get', 'cantonCode']];
  const matchExpressionPush = () => {
    for (const row of deduplicatedData) {
      const colorValue = row['colorValue'];
      const geoId = row['geoId'];
      if (colorValue && geoId) {
        matchExpression.push(geoId, colorValue);
      }
    }
  };

  switch (layerSource) {
    case MapLayerSource.Canton:
      matchExpression = ['match', ['get', 'cantonCode']];
      matchExpressionPush();
      map?.setPaintProperty(tileSetName, FILL_OPACITY, OPACITY_MAX);
      break;
    case MapLayerSource.Municipality:
      matchExpression = ['match', ['get', 'municipalityId']];
      matchExpressionPush();
      break;

    default:
      matchExpression = ['match', ['get', 'onrp']];
      matchExpressionPush();
      break;
  }
  matchExpression.push('rgba(0, 0, 0, 0)');
  map?.setPaintProperty(tileSetName, 'fill-color', matchExpression);
  if (layerSource !== MapLayerSource.Municipality) {
    map?.setPaintProperty(tileSetHighlightName, 'line-color', [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      LINECOLOR_HOVER,
      LINECOLOR_HOVER,
    ]);
  }
};
const handleClickEvent = (currentMap: Map, layerTileSetName: MapLayerName): void => {
  currentMap.on('click', layerTileSetName, async (e: MapMouseEvent) => {
    const feature = e.features?.[0] ?? null;
    const isFeatureChanged =
      feature &&
      (feature.properties?.cantonCode !== polygon.value.current?.properties?.cantonCode ||
        feature.properties?.municipalityId !== polygon.value.current?.properties?.municipalityId ||
        feature.properties?.onrp !== polygon.value.current?.properties?.onrp);

    if (isFeatureChanged) {
      // TODO: temporary RUM tracking example to collect the data
      captureAction(CustomAction.MapClick, {
        featureId: feature.id ?? '',
      });

      mapStore.setLoading(true);
      mapStore.setCurrentLevel(mapLayerToGeoLevel[layerTileSetName]);
      await pushLocation(EventSource.Map, feature.id);
      emit('mapInteraction');
    }
  });
};

const updateLayer = () => {
  if (!map || !polygon.value.current) return;

  setLayerVisibility(
    map as Map,
    [MapLayerName.CantonTileSet, MapLayerName.MunicipalityTileSet, MapLayerName.TownTileSet],
    false,
  );

  const { swLon, swLat, neLon, neLat } = polygon.value.current.properties ?? {};
  const avgLat: number = (parseFloat(swLat) + parseFloat(neLat)) / 2;
  const avgLong: number = (parseFloat(swLon) + parseFloat(neLon)) / 2;
  map.flyTo({ center: [avgLong, avgLat], zoom: levelMinZoom.value });

  const activeCanton = polygon.value.current.properties?.cantonCode;
  if (activeCanton) {
    updatePolygonHiding(true);
    switch (currentLevel.value) {
      case MapGeoLevel.Canton:
        updateCantonLayer(activeCanton);
        break;
      case MapGeoLevel.Municipality:
        updateMunicipalityLayer(activeCanton);
        break;
      case MapGeoLevel.Town:
        clearHighlightLayer(map as Map);
        updateTownLayer(activeCanton);
        break;
      default:
        break;
    }
  }
};
const updatePolygon = (mapLevel: MapGeoLevel, response: PriceDistributionResponse) => {
  if (response?.results) {
    setLegendData(response?.results, mapLevel);
    if (mapLevel === MapGeoLevel.Canton) {
      addLayerColorDistribution(
        MapLayerName.MunicipalityTileSet,
        MapLayerSource.Municipality,
        MapLayerHighlightName.MunicipalityHighlight,
        response?.results ?? null,
      );
    } else {
      addLayerColorDistribution(
        MapLayerName.TownTileSet,
        MapLayerSource.Town,
        MapLayerHighlightName.TownHighlight,
        response?.results ?? null,
      );
    }
  }
};
const fetchAndSetPolygonData = async (
  level: LevelValue,
  geoLevel: MapGeoLevel,
  levelId: string,
): Promise<void> => {
  const townData = await getPriceDistribution({
    level: level,
    levelId: parseInt(levelId),
    category: propertyTypeToValue[propertyType.value],
    culture: culture.value,
  });
  updatePolygon(geoLevel, townData);
};
const updateCantonLayer = async (activeCanton: string) => {
  if (map) {
    addHighlightLayer(map as Map, polygon.value.current);
    map.setPaintProperty(MapLayerName.MunicipalityTileSet, FILL_OPACITY, [
      'case',
      ['==', ['get', 'cantonCode'], activeCanton],
      OPACITY_MAX,
      0,
    ]);
    setLayerVisibility(
      map as Map,
      [MapLayerName.MunicipalityTileSet, MapLayerHighlightName.MunicipalityHighlight],
      true,
    );

    // show respective town if user zooms in
    map.setPaintProperty(MapLayerName.TownTileSet, FILL_OPACITY, [
      'case',
      ['==', ['get', 'cantonCode'], activeCanton],
      OPACITY_MAX,
      0,
    ]);

    setLayerVisibility(
      map as Map,
      [MapLayerName.CantonTileSet, MapLayerHighlightName.CantonHighlight], // MAYBE ADD MapLayerName.TownTileSet again
      false,
    );

    map?.setPaintProperty(MapLayerName.TownTileSet, FILL_OPACITY, [
      'case',
      ['==', ['get', 'cantonCode'], activeCanton],
      OPACITY_MAX,
      0,
    ]);

    setLayerVisibility(
      map as Map,
      [MapLayerHighlightName.TownHighlight, MapLayerName.TownTileSet],
      true,
    );
  }
};
const updateMunicipalityLayer = async (activeCanton: string) => {
  if (map) {
    addHighlightLayer(map as Map, polygon.value.current);
    map.setPaintProperty(MapLayerName.TownTileSet, FILL_OPACITY, [
      'case',
      ['==', ['get', 'cantonCode'], activeCanton],
      OPACITY_MAX,
      0,
    ]);
    map.setPaintProperty(MapLayerName.MunicipalityTileSet, FILL_OPACITY, [
      'case',
      ['==', ['get', 'cantonCode'], activeCanton],
      OPACITY_MAX,
      0,
    ]);
    setLayerVisibility(
      map as Map,
      [MapLayerHighlightName.TownHighlight, MapLayerName.TownTileSet],
      true,
    );
    setLayerVisibility(
      map as Map,
      [
        MapLayerName.CantonTileSet,
        MapLayerHighlightName.CantonHighlight,
        MapLayerName.MunicipalityTileSet,
        MapLayerHighlightName.MunicipalityHighlight,
      ],
      false,
    );
  }
};
const updateTownLayer = async (activeCanton: string) => {
  if (!map) {
    return;
  }

  addHighlightLayer(map as Map, polygon.value.current);

  map?.setPaintProperty(MapLayerName.TownTileSet, FILL_OPACITY, [
    'case',
    ['==', ['get', 'cantonCode'], activeCanton],
    OPACITY_MAX,
    0,
  ]);
  map?.setPaintProperty(MapLayerName.MunicipalityTileSet, FILL_OPACITY, [
    'case',
    ['==', ['get', 'cantonCode'], activeCanton],
    OPACITY_MAX,
    0,
  ]);
  setLayerVisibility(
    map as Map,
    [MapLayerHighlightName.TownHighlight, MapLayerName.TownTileSet],
    true,
  );
  setLayerVisibility(
    map as Map,
    [
      MapLayerName.CantonTileSet,
      MapLayerHighlightName.CantonHighlight,
      MapLayerName.MunicipalityTileSet,
      MapLayerHighlightName.MunicipalityHighlight,
    ],
    false,
  );
};

const checkZoomLevel = (): void => {
  if (!map) {
    return;
  }
  const zoomLevel = map.getZoom();
  if (zoomLevel <= MAP_MAX_ZOOM) {
    map.setPaintProperty(MapLayerName.CantonTileSet, FILL_OPACITY, OPACITY_MAX);

    setLayerVisibility(
      map as Map,
      [
        MapLayerName.CantonTileSet,
        MapLayerHighlightName.CantonHighlight,
        MapLayerName.MunicipalityTileSet,
        MapLayerHighlightName.MunicipalityHighlight,
      ],
      true,
    );
  }
  if (currentZoomLevel.value == MapGeoLevel.Country && priceDistributions.value.canton) {
    mapStore.setDataRange(priceDistributions.value.canton.range);
    mapStore.setLegendColors(sortLegendColors(priceDistributions.value.canton?.colorValues) ?? []);
  } else if (
    currentZoomLevel.value == MapGeoLevel.Canton &&
    selectedPriceDistributions.value.canton
  ) {
    mapStore.setDataRange(selectedPriceDistributions.value.canton.range);
    mapStore.setLegendColors(
      sortLegendColors(selectedPriceDistributions.value.canton?.colorValues) ?? [],
    );
  } else if (
    currentZoomLevel.value == MapGeoLevel.Municipality &&
    selectedPriceDistributions.value.municipality
  ) {
    mapStore.setDataRange(selectedPriceDistributions.value.municipality.range);
    mapStore.setLegendColors(
      sortLegendColors(selectedPriceDistributions.value.municipality?.colorValues) ?? [],
    );
  }
};
const disableScrollZoomOnMapbox = (): void => {
  const bottomLeftControl = document.querySelector('.mapboxgl-ctrl-bottom-left');
  const bottomRightControl = document.querySelector('.mapboxgl-ctrl-bottom-right');
  const handleWheelEvent = (event: any) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
    }
  };
  if (bottomLeftControl)
    bottomLeftControl.addEventListener('wheel', handleWheelEvent, {
      passive: false,
    });
  if (bottomRightControl)
    bottomRightControl.addEventListener('wheel', handleWheelEvent, {
      passive: false,
    });
};
const clickEventsRegister = (): void => {
  if (map) {
    handleClickEvent(map as Map, MapLayerName.CantonTileSet);
    handleClickEvent(map as Map, MapLayerName.MunicipalityTileSet);
    handleClickEvent(map as Map, MapLayerName.TownTileSet);
  }
};
const initializeMap = async () => {
  mapStore.setIsMapLoading(true);
  await updateMapAndData(false, true);

  if (!window.WebGLRenderingContext) {
    mapStore.setIsMapLoading(false);
    console.warn('Mapbox is not supported in this browser');
    return;
  }

  map = new Map({
    container: document.getElementById('mapContainer') as HTMLElement,
    center: SWITZERLAND_COORDINATE,
    zoom: zoom.value,
    minZoom: MAP_MIN_ZOOM,
    maxZoom: MAP_MAX_ZOOM,
    style: import.meta.env.VUE_MAPBOX_STYLE_URL,
  });

  map
    .on('load', async () => {
      const refreshColor = true;
      setInitialProperties(false, refreshColor);
      clickEventsRegister();
      disableScrollZoomOnMapbox();
    })
    .on('wheel', () => {
      checkZoomLevel();
    })
    .on('zoomend', () => {
      checkZoomLevel();
    })
    .on('error', (e: any) => {
      const errorMessage = e.source?.url
        ? `Failed to fetch ${e.source.url}`
        : e.message || 'Unknown error';
      console.warn(`Mapbox Error: ${errorMessage}`);
    });

  map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
};
const setInitialProperties = async (reload = false, refreshColor: boolean) => {
  setInitialPaintProperties(map as Map, locale.value);
  loadCantonLayer(true);
  loadMunicipalityLayer(refreshColor);
  loadTownLayer(refreshColor);

  if (reload) {
    // if culture changes, need to push url again to trigger the whole flow for client-side transition
    await pushLocation(EventSource.Reload);
  } else {
    updateLayer();
    mapStore.setIsMapLoading(false);
  }
};
const setLegendData = (priceDistributionData: PriceDistributionData, levelClicked: MapGeoLevel) => {
  if (currentLevel.value === MapGeoLevel.Country && priceDistributions.value.canton?.range) {
    mapStore.setDataRange(priceDistributions.value.canton?.range);
    mapStore.setLegendColors(sortLegendColors(priceDistributions.value.canton?.colorValues) ?? []);
  } else {
    if (
      currentLevel.value === levelClicked ||
      (currentLevel.value === MapGeoLevel.Town && levelClicked === MapGeoLevel.Municipality)
    ) {
      mapStore.setDataRange(priceDistributionData.range);
      mapStore.setLegendColors(sortLegendColors(priceDistributionData?.colorValues) ?? []);
    }
  }
  mapStore.setSelectedPriceDistributions(levelClicked, priceDistributionData);
};
</script>
<style lang="scss" scoped>
@use '@/styles/variables' as *;

.map-canvas {
  width: 100%;
  height: calc(100dvh - #{$header-height-sm});

  @include grid-media-query(sm) {
    width: 100%;
    height: 100%;
  }
}
</style>
